/**
 * API Service - NOW USING DEPLOYED EDGE FUNCTIONS! 🚀
 */

import { supabase } from '../lib/supabase';
import { formatDuration } from '../utils/timeFormat';
import type { Project, Vehicle } from '../types/database';

// Use environment variable with fallback for test environments
const getSupabaseFunctionsUrl = (): string => {
  // In test environment, use the known project URL
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
    return 'https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1';
  }
  
  // In browser environment, try to get from Vite env
  try {
    if (typeof window !== 'undefined' && import.meta?.env?.VITE_SUPABASE_URL) {
      return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
    }
  } catch (e) {
    // Fallback if import.meta is not available in test environment
  }
  
  // Default fallback
  return 'https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1';
};

const SUPABASE_FUNCTIONS_URL = getSupabaseFunctionsUrl();
// Production-ready backend URL resolution with HTTPS compliance
const getBackendURL = (): string => {
  // Detect production environment (HTTPS context) 
  const isProductionHTTPS = typeof window !== 'undefined' && 
    window.location.protocol === 'https:' &&
    !window.location.hostname.includes('localhost');
  
  // Production: Always use HTTPS-compliant Edge Function direct access
  if (isProductionHTTPS) {
    console.log('🔒 Production HTTPS mode - using Edge Function direct access');
    return `${SUPABASE_FUNCTIONS_URL}`;
  }
  
  // Development: Direct backend access with settings override
  if (typeof window !== 'undefined') {
    const savedSettings = localStorage.getItem('vrp-backend-settings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        
        // Environment defaults
        if (settings.useEnvironmentDefaults && import.meta.env.VITE_FASTAPI_URL) {
          return import.meta.env.VITE_FASTAPI_URL;
        }
        
        // Custom development settings
        if (settings.backendIP && settings.fastApiPort) {
          return `http://${settings.backendIP}:${settings.fastApiPort}`;
        }
      } catch (e) {
        console.warn('Failed to parse saved backend settings:', e);
      }
    }
  }
  
  // No fallback - must be configured via environment variables
  const backendUrl = import.meta.env.VITE_FASTAPI_URL;
  if (!backendUrl) {
    console.error('❌ VITE_FASTAPI_URL environment variable not configured');
    throw new Error('Backend URL not configured. Please set VITE_FASTAPI_URL in Cloudflare Pages environment variables.');
  }
  return backendUrl;
};

// Note: FASTAPI_URL is dynamic and resolved per request via getBackendURL()

class APIClient {
  // Entity API namespaces
  vehicles = {
    getAll: async (projectId: number): Promise<any[]> => {
      return this.getVehicles(projectId);
    },
    create: async (projectId: number, data: any): Promise<any> => {
      return this.createVehicle({ ...data, project_id: projectId });
    },
    update: async (projectId: number, id: number, data: any): Promise<any> => {
      return this.updateVehicle(id, data, projectId);
    },
    delete: async (projectId: number, id: number): Promise<void> => {
      return this.deleteVehicle(id, projectId);
    }
  };

  locations = {
    getAll: async (projectId: number): Promise<any[]> => {
      return this.getLocations(projectId);
    },
    create: async (projectId: number, data: any): Promise<any> => {
      return this.createLocation({ ...data, project_id: projectId });
    },
    update: async (projectId: number, id: number, data: any): Promise<any> => {
      return this.updateLocation(id, data, projectId);
    },
    delete: async (projectId: number, id: number): Promise<void> => {
      return this.deleteLocation(id, projectId);
    }
  };

  jobs = {
    getAll: async (projectId: number, scenarioId?: number): Promise<any[]> => {
      return this.getJobs(projectId, { scenario_id: scenarioId });
    },
    create: async (projectId: number, data: any): Promise<any> => {
      return this.createJob({ ...data, project_id: projectId });
    },
    update: async (projectId: number, id: number, data: any): Promise<any> => {
      return this.updateJob(id, data, projectId);
    },
    delete: async (projectId: number, id: number): Promise<void> => {
      return this.deleteJob(id, projectId);
    }
  };

  products = {
    getAll: async (projectId: number): Promise<any[]> => {
      // TODO: Implement getProducts method
      console.warn('[API] products.getAll not implemented');
      return [];
    },
    create: async (projectId: number, data: any): Promise<any> => {
      // TODO: Implement createProduct method
      throw new Error('Product creation not implemented');
    },
    update: async (projectId: number, id: number, data: any): Promise<any> => {
      // TODO: Implement updateProduct method
      throw new Error('Product update not implemented');
    },
    delete: async (projectId: number, id: number): Promise<void> => {
      // TODO: Implement deleteProduct method
      throw new Error('Product deletion not implemented');
    }
  };

  shipments = {
    getAll: async (projectId: number, scenarioId?: number): Promise<any[]> => {
      // TODO: Implement getShipments method
      console.warn('[API] shipments.getAll not implemented');
      return [];
    },
    create: async (projectId: number, data: any): Promise<any> => {
      // TODO: Implement createShipment method
      throw new Error('Shipment creation not implemented');
    },
    update: async (projectId: number, id: number, data: any): Promise<any> => {
      // TODO: Implement updateShipment method
      throw new Error('Shipment update not implemented');
    },
    delete: async (projectId: number, id: number): Promise<void> => {
      // TODO: Implement deleteShipment method
      throw new Error('Shipment deletion not implemented');
    }
  };

  skills = {
    getAll: async (projectId: number): Promise<any[]> => {
      return this.getSkills();
    },
    create: async (projectId: number, data: any): Promise<any> => {
      return this.createSkill(data);
    },
    update: async (projectId: number, id: number, data: any): Promise<any> => {
      return this.updateSkill(id, data);
    },
    delete: async (projectId: number, id: number): Promise<void> => {
      return this.deleteSkill(id);
    }
  };

  scenarios = {
    getAll: async (projectId: number): Promise<any[]> => {
      return this.getScenarios(projectId);
    },
    create: async (projectId: number, data: any): Promise<any> => {
      return this.createScenario({ ...data, project_id: projectId });
    },
    update: async (projectId: number, id: number, data: any): Promise<any> => {
      return this.updateScenario(id, data);
    },
    delete: async (projectId: number, id: number): Promise<void> => {
      return this.deleteScenario(id);
    }
  };

  datasets = {
    getAll: async (projectId: number, scenarioId?: number): Promise<any[]> => {
      if (!scenarioId) {
        console.warn('[API] datasets.getAll called without scenarioId, returning empty array');
        return [];
      }
      return this.getDatasets(scenarioId);
    },
    create: async (projectId: number, data: any): Promise<any> => {
      return this.createDataset(data);
    },
    update: async (projectId: number, id: number, data: any): Promise<any> => {
      return this.updateDataset(id, data);
    },
    delete: async (projectId: number, id: number): Promise<void> => {
      return this.deleteDataset(id);
    }
  };

  optimizations = {
    getAll: async (projectId: number, scenarioId?: number): Promise<any[]> => {
      return this.getOptimizationRuns(projectId, { scenario_id: scenarioId });
    },
    create: async (projectId: number, data: any): Promise<any> => {
      return this.runOptimization(data, projectId);
    },
    update: async (projectId: number, id: number, data: any): Promise<any> => {
      throw new Error('Optimization runs cannot be updated');
    },
    delete: async (projectId: number, id: number): Promise<void> => {
      throw new Error('Optimization runs cannot be deleted');
    }
  };

  routes = {
    getAll: async (projectId: number, scenarioId?: number): Promise<any[]> => {
      return this.getRouteSummaries(projectId);
    },
    create: async (projectId: number, data: any): Promise<any> => {
      throw new Error('Routes are created through optimization runs');
    },
    update: async (projectId: number, id: number, data: any): Promise<any> => {
      throw new Error('Routes cannot be updated directly');
    },
    delete: async (projectId: number, id: number): Promise<void> => {
      throw new Error('Routes cannot be deleted directly');
    }
  };

  // Public Edge Function call (no authentication required)
  private async callPublicEdgeFunction(functionName: string, options: RequestInit = {}): Promise<any> {
    const url = `${SUPABASE_FUNCTIONS_URL}/${functionName}`;
    console.log(`🌐 Public Edge Function Call:`, {
      url,
      method: options.method || 'GET',
      public: true
    });

    const startTime = Date.now();
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      const duration = Date.now() - startTime;
      console.log(`⚡ ${functionName} (public) response: ${response.status} in ${duration.toFixed(2)}ms`);

      if (!response.ok) {
        let errorMessage: string;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || `HTTP ${response.status}`;
        } catch {
          errorMessage = `HTTP ${response.status} ${response.statusText}`;
        }
        
        console.error(`❌ ${functionName} (public) error:`, {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage,
          duration: duration.toFixed(2) + 'ms'
        });
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result;
      
    } catch (fetchError) {
      console.error(`💥 Public fetch error for ${functionName}:`, fetchError);
      throw fetchError;
    }
  }

  private async callEdgeFunction(functionName: string, options: RequestInit = {}): Promise<any> {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    console.log('🔐 Auth Debug:', {
      hasSession: !!session,
      hasAccessToken: !!session?.access_token,
      tokenLength: session?.access_token?.length,
      sessionError
    });
    
    if (!session?.access_token) {
      // Return null instead of throwing error to prevent console noise during initial load
      console.warn('⚠️ No authentication token available - user may not be signed in yet');
      return null;
    }
    
    const url = `${SUPABASE_FUNCTIONS_URL}/${functionName}`;
    console.log(`🚀 Edge Function Call:`, {
      url,
      method: options.method || 'GET',
      hasAuth: !!session.access_token
    });

    const startTime = Date.now();
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'authorization': `Bearer ${session.access_token}`,
      };

      // Add custom headers from options if method is POST/PUT/PATCH
      if (options.method && ['POST', 'PUT', 'PATCH'].includes(options.method)) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      const duration = Date.now() - startTime;
      console.log(`⚡ ${functionName} response: ${response.status} in ${duration.toFixed(2)}ms`);

      if (!response.ok) {
        let errorMessage: string;
        let errorData: any = {};
        try {
          errorData = await response.json();
          errorMessage = errorData.error || errorData.message || `HTTP ${response.status}`;
        } catch {
          errorMessage = `HTTP ${response.status} ${response.statusText}`;
        }
        
        console.error(`❌ ${functionName} error:`, {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage,
          fullErrorData: errorData,
          duration: duration.toFixed(2) + 'ms'
        });
        
        // Additional detailed error logging
        console.error(`❌ ${functionName} detailed error response:`, JSON.stringify(errorData, null, 2));
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      // Log structured response for debugging
      if (result.meta) {
        console.log(`📊 ${functionName} metadata:`, result.meta);
      }
      
      return result;
      
    } catch (fetchError) {
      console.error(`💥 Fetch error for ${functionName}:`, fetchError);
      throw fetchError;
    }
  }

  private async callFastAPI(endpoint: string, options: RequestInit = {}): Promise<any> {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    console.log('🔐 FastAPI Auth Debug:', {
      hasSession: !!session,
      hasAccessToken: !!session?.access_token,
      tokenLength: session?.access_token?.length,
      sessionError,
      endpoint
    });
    
    if (!session?.access_token) {
      console.error('❌ No authentication token available for FastAPI');
      throw new Error('No authentication token available');
    }

    const url = `${getBackendURL()}${endpoint}`;
    console.log(`🚀 FastAPI Call:`, {
      url,
      method: options.method || 'GET',
      hasAuth: !!session.access_token
    });

    const startTime = Date.now();
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          ...options.headers,
        },
      });

      const duration = Date.now() - startTime;
      console.log(`⚡ FastAPI response: ${response.status} in ${duration}ms`);

      if (!response.ok) {
        let errorMessage: string;
        let errorDetail: any = null;
        try {
          const errorData = await response.json();
          errorDetail = errorData;
          errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}`;
          
          // For 422 errors, log the detailed validation errors
          if (response.status === 422 && errorData.detail) {
            console.error('🔍 FastAPI 422 Validation Errors:', errorData.detail);
            if (Array.isArray(errorData.detail)) {
              errorData.detail.forEach((error: any, index: number) => {
                console.error(`  ${index + 1}. ${error.msg} at ${error.loc?.join('.')}`);
              });
            }
          }
        } catch {
          errorMessage = `HTTP ${response.status} ${response.statusText}`;
        }
        
        console.error(`❌ FastAPI error:`, {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage,
          duration: duration + 'ms',
          detail: errorDetail
        });
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log(`✅ FastAPI success:`, { endpoint, duration: duration + 'ms' });
      return result;
      
    } catch (fetchError) {
      console.error(`💥 FastAPI fetch error:`, fetchError);
      throw fetchError;
    }
  }

  // Projects API - Using Edge Functions
  async getProjects(): Promise<Project[]> {
    const response = await this.callEdgeFunction('projects');
    
    // Unwrap the response if it's in the wrapped format
    if (response && response.success && Array.isArray(response.data)) {
      return response.data;
    }
    
    // Return empty array if no valid response
    return [];
  }

  async createProject(project: {
    name: string;
    description?: string;
    currency?: string;
    project_type?: string;
    industry?: string;
    geographic_region?: string;
    priority?: 'low' | 'medium' | 'high' | 'critical';
    estimated_vehicles?: number;
    estimated_jobs?: number;
    planning_horizon_days?: number;
    contact_person?: string;
    contact_email?: string;
    contact_phone?: string;
    tags?: string[];
    notes?: string;
  }): Promise<Project> {
    return this.callEdgeFunction('projects', {
      method: 'POST',
      body: JSON.stringify(project),
    });
  }

  async updateProject(id: number, project: {
    name?: string;
    description?: string;
    currency?: string;
    project_type?: string;
    industry?: string;
    geographic_region?: string;
    priority?: 'low' | 'medium' | 'high' | 'critical';
    estimated_vehicles?: number;
    estimated_jobs?: number;
    planning_horizon_days?: number;
    contact_person?: string;
    contact_email?: string;
    contact_phone?: string;
    tags?: string[];
    notes?: string;
  }): Promise<Project> {
    const params = new URLSearchParams({ id: id.toString() });
    return this.callEdgeFunction(`projects?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify(project),
    });
  }

  // Vehicles API - Using Edge Functions
  async getVehicles(projectId: number, options?: {
    search?: string;
    page?: number;
    limit?: number;
    scenario_id?: number;
    dataset_name?: string;
    dataset_version?: number;
    dataset_id?: number;
    include_all_project_data?: boolean;
  }): Promise<Vehicle[]> {
    const params = new URLSearchParams({ project_id: projectId.toString() });
    
    if (options?.search) params.append('search', options.search);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.scenario_id) {
      // Defensive check to ensure scenario_id is a number, not an object
      if (typeof options.scenario_id === 'object') {
        console.error('❌ [API] scenario_id is an object instead of number:', options.scenario_id);
        // Additional debugging info
        console.error('❌ [API] Object keys:', Object.keys(options.scenario_id));
        console.error('❌ [API] Object values:', Object.values(options.scenario_id));
        console.error('❌ [API] Full options object:', options);
        throw new Error('Invalid scenario_id: expected number, got object. Check scenario context usage.');
      }
      // Also check for undefined/null/NaN values
      if (options.scenario_id == null || isNaN(options.scenario_id)) {
        console.error('❌ [API] scenario_id is invalid:', options.scenario_id);
        throw new Error('Invalid scenario_id: must be a valid number');
      }
      params.append('scenario_id', options.scenario_id.toString());
    }
    if (options?.dataset_id) {
      // Defensive check to ensure dataset_id is a number, not an object
      if (typeof options.dataset_id === 'object') {
        console.error('❌ [API] dataset_id is an object instead of number:', options.dataset_id);
        // Additional debugging info
        console.error('❌ [API] Object keys:', Object.keys(options.dataset_id));
        console.error('❌ [API] Object values:', Object.values(options.dataset_id));
        console.error('❌ [API] Full options object:', options);
        throw new Error('Invalid dataset_id: expected number, got object. Check dataset context usage.');
      }
      // Also check for undefined/null/NaN values
      if (options.dataset_id == null || isNaN(options.dataset_id)) {
        console.error('❌ [API] dataset_id is invalid:', options.dataset_id);
        throw new Error('Invalid dataset_id: must be a valid number');
      }
      params.append('dataset_id', options.dataset_id.toString());
    }
    if (options?.dataset_name) params.append('dataset_name', options.dataset_name);
    if (options?.dataset_version) params.append('dataset_version', options.dataset_version.toString());
    if (options?.include_all_project_data) params.append('include_all_project_data', options.include_all_project_data.toString());

    const response = await this.callEdgeFunction(`vehicles?${params.toString()}`);
    
    // Unwrap the response if it's in the wrapped format
    if (response && response.success && Array.isArray(response.data)) {
      return response.data;
    }
    
    // Return empty array if no valid response
    return [];
  }

  async getVehicle(vehicleId: number, projectId: number): Promise<Vehicle> {
    const params = new URLSearchParams({
      project_id: projectId.toString(),
      id: vehicleId.toString()
    });
    
    return this.callEdgeFunction(`vehicles?${params.toString()}`);
  }

  async createVehicle(vehicle: {
    description: string;
    profile?: string;
    start_location_id?: number;
    end_location_id?: number;
    start_lon?: number;
    start_lat?: number;
    end_lon?: number;
    end_lat?: number;
    capacity?: number[];
    skills?: number[];
    costs?: {
      fixed?: number;
      per_hour?: number;
      per_km?: number;
    };
    speed_factor?: number;
    max_tasks?: number;
    max_travel_time?: number;
    max_distance?: number;
    project_id: number;
    scenario_id: number;  // REQUIRED: Must specify scenario context
    dataset_id: number;   // REQUIRED: Must specify dataset context
    dataset_name?: string;
    dataset_version?: number;
  }): Promise<Vehicle> {
    // Validate required context before API call
    if (!vehicle.scenario_id) {
      throw new Error('scenario_id is required for vehicle creation');
    }
    if (!vehicle.dataset_id) {
      throw new Error('dataset_id is required for vehicle creation');
    }
    return this.callEdgeFunction('vehicles', {
      method: 'POST',
      body: JSON.stringify(vehicle),
    });
  }

  async updateVehicle(vehicleId: number, vehicle: {
    description?: string;
    profile?: string;
    start_location_id?: number;
    end_location_id?: number;
    start_lon?: number;
    start_lat?: number;
    end_lon?: number;
    end_lat?: number;
    capacity?: number[];
    skills?: number[];
    costs?: {
      fixed?: number;
      per_hour?: number;
      per_km?: number;
    };
    speed_factor?: number;
    max_tasks?: number;
    max_travel_time?: number;
    max_distance?: number;
  }, projectId: number): Promise<Vehicle> {
    const params = new URLSearchParams({
      id: vehicleId.toString(),
      project_id: projectId.toString()
    });
    
    console.log('🚗 UPDATE Vehicle API Call:', `vehicles?${params.toString()}`);
    return this.callEdgeFunction(`vehicles?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify(vehicle),
    });
  }

  async deleteVehicle(vehicleId: number, projectId: number): Promise<void> {
    const params = new URLSearchParams({
      id: vehicleId.toString(),
      project_id: projectId.toString()
    });
    
    console.log('🚗 DELETE Vehicle API Call:', `vehicles?${params.toString()}`);
    return this.callEdgeFunction(`vehicles?${params.toString()}`, {
      method: 'DELETE',
    });
  }

  async bulkCreateVehicles(vehicles: any[], projectId: number, scenarioId: number, datasetId: number): Promise<Vehicle[]> {
    // Validate required context
    if (!scenarioId) {
      throw new Error('scenario_id is required for bulk vehicle creation');
    }
    if (!datasetId) {
      throw new Error('dataset_id is required for bulk vehicle creation');
    }
    
    return this.callEdgeFunction('vehicles/bulk', {
      method: 'POST',
      body: JSON.stringify({ 
        vehicles, 
        project_id: projectId,
        scenario_id: scenarioId,
        dataset_id: datasetId
      }),
    });
  }

  // Optimisation Engine Integration - Using Edge Function for health check
  async checkOptimisationEngineHealth(): Promise<{ engine_available: boolean; status: string; solver_url: string }> {
    try {
      const result = await this.callEdgeFunction('optimization');
      
      // Handle both success and error responses - check vroom_available in data
      if (result?.data) {
        const isAvailable = result.data.vroom_available === true;
        return {
          engine_available: isAvailable,
          status: isAvailable ? 'Optimisation Engine Healthy' : 'Optimisation Engine Unreachable',
          solver_url: import.meta.env.VITE_VROOM_SOLVER_URL || 'http://34.79.187.253:30010'
        };
      }
      
      // Fallback for cases where no data is returned
      return {
        engine_available: false,
        status: 'Optimisation Engine Error - No Response Data',
        solver_url: import.meta.env.VITE_VROOM_SOLVER_URL || 'http://34.79.187.253:30010'
      };
    } catch (error) {
      console.error('❌ Health check API call failed:', error);
      return {
        engine_available: false,
        status: `Optimisation Engine Error - ${error instanceof Error ? error.message : 'Unknown error'}`,
        solver_url: import.meta.env.VITE_VROOM_SOLVER_URL || 'http://34.79.187.253:30010'
      };
    }
  }

  // Legacy method for backwards compatibility
  async checkVROOMHealth(): Promise<{ vroom_available: boolean; status: string; solver_url: string }> {
    const result = await this.checkOptimisationEngineHealth();
    return {
      vroom_available: result.engine_available,
      status: result.status,
      solver_url: result.solver_url
    };
  }

  // Test optimisation engine
  async testOptimisationEngine(): Promise<any> {
    return this.callEdgeFunction('optimization', {
      method: 'POST',
    });
  }

  // Legacy method for backwards compatibility
  async testVROOMOptimization(): Promise<any> {
    return this.testOptimisationEngine();
  }

  // Direct optimisation with full dataset context integration
  async runDirectOptimization(request: {
    vehicles: Array<{
      id: number;
      start: [number, number];
      end: [number, number];
      capacity: number[];
      skills: number[];
      time_window: [number, number];
      costs: {
        fixed: number;
        per_hour: number;
        per_km: number;
      };
      max_distance?: number;
      max_travel_time?: number;
    }>;
    jobs: Array<{
      id: number;
      location: [number, number];
      service: number;
      delivery?: number[];
      pickup?: number[];
      skills: number[];
      priority: number;
      time_windows?: [number, number][];
    }>;
    options: {
      g: boolean;
      useMatrix: boolean;
      threads?: number;
    };
    // REQUIRED context for dataset-aware optimization
    scenario_id: number;
    dataset_id: number;
    dataset_name: string;
    dataset_version: string;
    project_id: number;
  }): Promise<any> {
    // Validate required context parameters
    if (!request.scenario_id || !request.dataset_id || !request.project_id) {
      throw new Error('Optimisation requires full context: scenario_id, dataset_id, and project_id');
    }

    console.log('🎯 [API] Running optimisation with full context:', {
      scenario_id: request.scenario_id,
      dataset_id: request.dataset_id,
      dataset_name: request.dataset_name,
      project_id: request.project_id,
      vehicles: request.vehicles.length,
      jobs: request.jobs.length
    });

    return this.callEdgeFunction('optimization', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Legacy optimisation - Using Edge Function with dataset context
  async runOptimization(request: {
    project_id: number;
    scenario_id?: number;
    dataset_name?: string;
    dataset_version?: number;
  }): Promise<any> {
    return this.callEdgeFunction('optimization', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Optimization Results API - Via Edge Functions
  async getOptimizationRuns(projectId: number, options?: {
    limit?: number;
    status?: string;
  }): Promise<any> {
    console.log('🔍 Getting optimization runs for project:', projectId);
    
    try {
      // SECURE: Direct Supabase query with RLS enforcement
      let query = supabase
        .from('optimization_runs')
        .select(`
          *,
          scenarios!inner(
            id,
            project_id,
            name
          )
        `)
        .eq('scenarios.project_id', projectId)
        .order('timestamp', { ascending: false });

      // Apply optional filters
      if (options?.status) {
        query = query.eq('status', options.status);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data: runs, error } = await query;

      if (error) {
        console.error('❌ Failed to get optimization runs (RLS or query error):', error);
        throw new Error(`Failed to access optimization runs: ${error.message}`);
      }

      console.log('✅ Secure optimization runs fetched:', runs?.length || 0);
      
      // Add converted fields for consistent display
      const runsWithConvertedFields = (runs || []).map((run: any) => ({
        ...run,
        total_distance_km: ((run.total_distance || 0) / 1000).toFixed(2),
        total_duration_minutes: Math.floor((run.total_duration || 0) / 60),
        project_id: projectId, // Ensure project context is clear
      }));
      
      return runsWithConvertedFields;

    } catch (error) {
      console.error('❌ getOptimizationRuns failed:', error);
      throw error;
    }
  }

  async getOptimizationRun(runId: number, projectId: number): Promise<any> {
    const params = new URLSearchParams({ project_id: projectId.toString() });
    return this.callEdgeFunction(`optimization-results/${runId}?${params.toString()}`);
  }

  async getOptimizationRunRoutes(runId: number, projectId: number): Promise<any> {
    console.log('🛣️ Getting routes for run:', runId, 'project:', projectId);
    
    try {
      // SECURE: Direct Supabase query with RLS enforcement including route_steps and job relationships
      // RLS policies automatically enforce user_has_project_access(project_id)
      const { data: routes, error } = await supabase
        .from('route_summaries')
        .select(`
          *,
          optimization_runs!inner(
            id, 
            project_id,
            scenario_id,
            timestamp,
            status
          ),
          route_steps(
            id,
            step_type,
            step_order,
            arrival_time,
            service_time,
            waiting_time,
            distance,
            job_id,
            lon,
            lat,
            load,
            violations,
            jobs(
              id,
              description,
              delivery,
              pickup,
              skills,
              priority
            )
          )
        `)
        .eq('optimization_run_id', runId)
        .eq('optimization_runs.project_id', projectId);

      if (error) {
        console.error('❌ Failed to get routes (RLS or query error):', error);
        throw new Error(`Failed to access routes: ${error.message}`);
      }

      console.log('✅ Secure routes fetched:', routes?.length || 0);
      
      if (routes && routes.length > 0) {
        console.log('🔍 Sample route structure keys:', Object.keys(routes[0]));
        console.log('📊 Routes with optimization run info loaded successfully');
      }
      
      return routes || [];

    } catch (error) {
      console.error('❌ getOptimizationRunRoutes failed:', error);
      throw error;
    }
  }

  async getLatestOptimizationRun(projectId: number): Promise<any> {
    console.log('🔍 Getting latest optimization run for project:', projectId);
    const runs = await this.getOptimizationRuns(projectId, { limit: 1, status: 'completed' });
    console.log('📊 Found optimization runs:', runs);
    
    if (runs && runs.length > 0) {
      const latestRun = runs[0];
      console.log('🎯 Latest run:', latestRun);
      
      try {
        const routes = await this.getOptimizationRunRoutes(latestRun.id, projectId);
        console.log('🛣️ Routes data:', routes);
        return {
          run: latestRun,
          routes: routes || []
        };
      } catch (error) {
        console.log('⚠️ Failed to get routes, falling back to basic data:', error);
        // Fallback: use basic data from the optimization run
        return {
          run: latestRun,
          routes: [] // Will be populated from raw_response if available
        };
      }
    }
    console.log('❌ No optimization runs found');
    return null;
  }

  // Get all route summaries for a project (from all optimization runs)
  async getRouteSummaries(projectId: number): Promise<any[]> {
    console.log('🔍 Getting route summaries for project:', projectId);
    
    // Enhanced security validation
    if (!projectId || projectId <= 0) {
      throw new Error('Invalid project ID provided');
    }

    try {
      // Get optimization runs first (with RLS enforcement)
      const runs = await this.getOptimizationRuns(projectId, { limit: 100 });
      if (!runs || runs.length === 0) {
        console.log('ℹ️ No optimization runs found for project:', projectId);
        return [];
      }

      console.log(`📊 Processing ${runs.length} optimization runs for route summaries`);
      const allRoutes: any[] = [];
      
      for (const run of runs) {
        try {
          // Security check: Ensure run belongs to the correct project
          if (run.project_id && run.project_id !== projectId) {
            console.warn(`⚠️ Project ID mismatch: run ${run.id} belongs to project ${run.project_id}, not ${projectId}`);
            continue;
          }

          const routes = await this.getOptimizationRunRoutes(run.id, projectId);
          if (routes && routes.length > 0) {
            // Add run info to each route with security context
            const routesWithRunInfo = routes.map((route: any) => ({
              ...route,
              optimization_run_id: run.id,
              run_timestamp: run.timestamp,
              run_status: run.status,
              project_id: projectId, // Explicit project context
              // Add converted fields for consistent display
              distance_km: ((route.distance || 0) / 1000).toFixed(2),
              duration_minutes: Math.floor((route.duration || 0) / 60),
              waiting_time_minutes: Math.floor((route.waiting_time || 0) / 60),
              service_time_formatted: formatDuration(route.service_time || 0),
            }));
            allRoutes.push(...routesWithRunInfo);
            console.log(`✅ Added ${routes.length} routes from run ${run.id}`);
          }
        } catch (error) {
          console.warn(`⚠️ Failed to get routes for run ${run.id}:`, error);
          // Continue processing other runs instead of failing completely
        }
      }
      
      console.log(`✅ Total route summaries collected: ${allRoutes.length}`);
      return allRoutes;

    } catch (error) {
      console.error('❌ getRouteSummaries failed:', error);
      throw error;
    }
  }

  // Get all route steps for a project - directly from Supabase via direct query
  async getRouteSteps(projectId: number): Promise<any[]> {
    console.log('🔍 Getting route steps for project:', projectId);
    
    try {
      // CRITICAL FIX: Two-step query approach due to Supabase nested filtering limitations
      
      // Step 1: Get optimization runs for this project
      const { data: optimizationRuns, error: runsError } = await supabase
        .from('optimization_runs')
        .select('id')
        .eq('project_id', projectId);
        
      if (runsError) {
        console.error('❌ Failed to fetch optimization runs:', runsError);
        return [];
      }
      
      const runIds = optimizationRuns?.map(run => run.id) || [];
      if (runIds.length === 0) {
        console.log('⚠️ No optimization runs found for project:', projectId);
        return [];
      }
      
      console.log('🔍 Found optimization run IDs for project', projectId, ':', runIds);
      
      // Step 2: Get route steps for these optimization runs
      const { data: steps, error } = await supabase
        .from('route_steps')
        .select(`
          *,
          route_summaries!inner (
            id,
            vehicle_id,
            optimization_run_id
          ),
          jobs (
            id,
            location_id,
            description
          )
        `)
        .in('route_summaries.optimization_run_id', runIds)
        .order('route_summary_id')
        .order('step_order');

      if (error) {
        console.error('❌ Supabase query error:', error);
        return [];
      }

      console.log('🔍 Raw route steps data sample:', steps?.[0]); // Debug first step structure
      console.log('🔍 Available fields in route step:', steps?.[0] ? Object.keys(steps[0]) : 'No data');
      console.log('🔍 Jobs data in step:', steps?.[0]?.jobs); // Debug jobs relationship
      console.log('🔍 Sample job steps:', steps?.filter(s => s.job_id)?.slice(0, 3));

      // If jobs relationship didn't work, fetch location_ids separately
      const jobIds = [...new Set(steps?.filter(s => s.job_id).map(s => s.job_id))];
      let jobsWithLocations: any = {};
      
      if (jobIds.length > 0 && (!steps?.[0]?.jobs || steps?.[0]?.jobs === null)) {
        console.log('🔍 Jobs relationship failed, fetching location_ids separately for job IDs:', jobIds);
        try {
          const { data: jobsData, error: jobsError } = await supabase
            .from('jobs')
            .select('id, location_id')
            .in('id', jobIds);
            
          if (!jobsError && jobsData) {
            jobsWithLocations = jobsData.reduce((acc: any, job: any) => {
              acc[job.id] = job;
              return acc;
            }, {});
            console.log('✅ Fetched jobs with location_ids:', jobsWithLocations);
          }
        } catch (err) {
          console.error('❌ Failed to fetch jobs separately:', err);
        }
      }

      // Fetch vehicle start/end location IDs for start/end step types
      const vehicleIds = [...new Set(steps?.map(s => s.vehicle_id))];
      let vehiclesWithLocations: any = {};
      
      if (vehicleIds.length > 0) {
        console.log('🚗 Fetching vehicle start/end location_ids for vehicle IDs:', vehicleIds);
        try {
          const { data: vehiclesData, error: vehiclesError } = await supabase
            .from('vehicles')
            .select('id, start_location_id, end_location_id')
            .in('id', vehicleIds);
            
          if (!vehiclesError && vehiclesData) {
            vehiclesWithLocations = vehiclesData.reduce((acc: any, vehicle: any) => {
              acc[vehicle.id] = vehicle;
              return acc;
            }, {});
            console.log('✅ Fetched vehicles with start/end location_ids:', vehiclesWithLocations);
          }
        } catch (err) {
          console.error('❌ Failed to fetch vehicles:', err);
        }
      }

      // Fetch location names for better display
      const locationIds = [...new Set([
        ...steps?.filter(s => s.location_id).map(s => s.location_id) || [],
        ...Object.values(jobsWithLocations).map((j: any) => j.location_id).filter(Boolean),
        ...Object.values(vehiclesWithLocations).flatMap((v: any) => [v.start_location_id, v.end_location_id]).filter(Boolean)
      ])];
      
      let locationsMap: any = {};
      if (locationIds.length > 0) {
        console.log('📍 Fetching location names for IDs:', locationIds);
        try {
          const { data: locationsData, error: locationsError } = await supabase
            .from('locations')
            .select('id, name, address')
            .in('id', locationIds);
            
          if (!locationsError && locationsData) {
            locationsMap = locationsData.reduce((acc: any, loc: any) => {
              acc[loc.id] = loc;
              return acc;
            }, {});
            console.log('✅ Fetched location names:', Object.keys(locationsMap).length);
          }
        } catch (err) {
          console.error('❌ Failed to fetch locations:', err);
        }
      }

      // Format the route steps with proper time formatting and units
      const formattedSteps = (steps || []).map(step => {
        // Helper function to format time from seconds to AM/PM
        const formatTimeToAMPM = (timeInSeconds: number): string => {
          if (timeInSeconds === null || timeInSeconds === undefined || isNaN(timeInSeconds)) {
            return 'N/A';
          }
          
          // VROOM returns absolute times as seconds since midnight (not relative times)
          // No need to add base offset - use the time value directly
          const absoluteTimeSeconds = timeInSeconds;
          
          const totalMinutes = Math.floor(absoluteTimeSeconds / 60);
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          
          // Convert to 12-hour format
          let displayHours = hours % 12;
          if (displayHours === 0) displayHours = 12;
          const ampm = hours < 12 ? 'AM' : 'PM';
          
          return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}${ampm}`;
        };

        const arrivalTime = step.arrival_time || 0;
        const setupTime = step.setup_time || 0;
        const serviceTime = step.service_time || 0;
        const waitingTime = step.waiting_time || 0;
        const departureTime = arrivalTime + setupTime + serviceTime + waitingTime;

        // Get location_id based on step type
        let locationId;
        if (step.step_type === 'start') {
          locationId = vehiclesWithLocations[step.vehicle_id]?.start_location_id;
        } else if (step.step_type === 'end') {
          locationId = vehiclesWithLocations[step.vehicle_id]?.end_location_id;
        } else {
          // Job step - get from jobs
          locationId = step.jobs?.location_id || jobsWithLocations[step.job_id]?.location_id;
        }

        // Get location name from our fetched locations
        const locationData = locationId ? locationsMap[locationId] : null;
        const locationName = locationData ? (locationData.name || locationData.address || `Location ${locationId}`) : 'Unknown Location';
        
        return {
          ...step,
          optimization_run_id: step.route_summaries?.optimization_run_id,
          vehicle_id: step.route_summaries?.vehicle_id || step.vehicle_id,
          location_id: locationId,
          location_name: locationName,
          arrival_time_formatted: formatTimeToAMPM(arrivalTime),
          departure_time_formatted: formatTimeToAMPM(departureTime),
          setup_time_minutes: Math.floor(setupTime / 60),
          service_time_minutes: Math.floor(serviceTime / 60),
          waiting_time_minutes: Math.floor(waitingTime / 60),
          duration_minutes: Math.floor((step.duration || 0) / 60),
          distance_km: ((step.distance || 0) / 1000).toFixed(2)
        };
      });

      console.log('✅ Route steps formatted successfully:', formattedSteps.length);
      return formattedSteps;
      
    } catch (error) {
      console.error('❌ Failed to get route steps from Supabase:', error);
      return [];
    }
  }

  // Scenarios API - Using Edge Functions
  async getScenarios(projectId: number, options?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<any> {
    const params = new URLSearchParams({ project_id: projectId.toString() });
    
    if (options?.search) params.append('search', options.search);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await this.callEdgeFunction(`scenarios?${params.toString()}`);
    
    // Unwrap the response if it's in the wrapped format
    if (response && response.success && Array.isArray(response.data)) {
      return response.data;
    }
    
    // Return empty array if no valid response
    return [];
  }

  async getScenario(scenarioId: number, projectId: number): Promise<any> {
    const params = new URLSearchParams({ project_id: projectId.toString() });
    return this.callEdgeFunction(`scenarios/${scenarioId}?${params.toString()}`);
  }

  async createScenario(scenario: {
    name: string;
    description?: string;
    project_id: number;
    start_date: string;
    planning_horizon_days: number;
  }): Promise<any> {
    return this.callEdgeFunction('scenarios', {
      method: 'POST',
      body: JSON.stringify(scenario),
    });
  }

  async updateScenario(scenarioId: number, projectId: number, updates: {
    name?: string;
    description?: string;
    start_date?: string;
    planning_horizon_days?: number;
  }): Promise<any> {
    const params = new URLSearchParams({ project_id: projectId.toString() });
    return this.callEdgeFunction(`scenarios/${scenarioId}?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteScenario(scenarioId: number, projectId: number): Promise<any> {
    const params = new URLSearchParams({ project_id: projectId.toString() });
    return this.callEdgeFunction(`scenarios/${scenarioId}?${params.toString()}`, {
      method: 'DELETE',
    });
  }

  // Locations API - Using Edge Functions with scenario context
  async getLocations(projectId: number, options?: {
    search?: string;
    page?: number;
    limit?: number;
    scenario_id?: number;
    dataset_name?: string;
    dataset_version?: number;
    dataset_id?: number;
  }): Promise<any> {
    const params = new URLSearchParams({ project_id: projectId.toString() });
    
    if (options?.search) params.append('search', options.search);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.scenario_id) {
      // Defensive check to ensure scenario_id is a number, not an object
      if (typeof options.scenario_id === 'object') {
        console.error('❌ [API] scenario_id is an object instead of number:', options.scenario_id);
        // Additional debugging info
        console.error('❌ [API] Object keys:', Object.keys(options.scenario_id));
        console.error('❌ [API] Object values:', Object.values(options.scenario_id));
        console.error('❌ [API] Full options object:', options);
        throw new Error('Invalid scenario_id: expected number, got object. Check scenario context usage.');
      }
      // Also check for undefined/null/NaN values
      if (options.scenario_id == null || isNaN(options.scenario_id)) {
        console.error('❌ [API] scenario_id is invalid:', options.scenario_id);
        throw new Error('Invalid scenario_id: must be a valid number');
      }
      params.append('scenario_id', options.scenario_id.toString());
    }
    if (options?.dataset_id) {
      // Defensive check to ensure dataset_id is a number, not an object
      if (typeof options.dataset_id === 'object') {
        console.error('❌ [API] dataset_id is an object instead of number:', options.dataset_id);
        // Additional debugging info
        console.error('❌ [API] Object keys:', Object.keys(options.dataset_id));
        console.error('❌ [API] Object values:', Object.values(options.dataset_id));
        console.error('❌ [API] Full options object:', options);
        throw new Error('Invalid dataset_id: expected number, got object. Check dataset context usage.');
      }
      // Also check for undefined/null/NaN values
      if (options.dataset_id == null || isNaN(options.dataset_id)) {
        console.error('❌ [API] dataset_id is invalid:', options.dataset_id);
        throw new Error('Invalid dataset_id: must be a valid number');
      }
      params.append('dataset_id', options.dataset_id.toString());
    }
    if (options?.dataset_name) params.append('dataset_name', options.dataset_name);
    if (options?.dataset_version) params.append('dataset_version', options.dataset_version.toString());

    const response = await this.callEdgeFunction(`locations?${params.toString()}`);
    
    // Unwrap the response if it's in the wrapped format
    if (response && response.success && Array.isArray(response.data)) {
      return response.data;
    }
    
    // Return empty array if no valid response
    return [];
  }

  async getLocation(locationId: number, projectId: number): Promise<any> {
    const params = new URLSearchParams({
      project_id: projectId.toString(),
      id: locationId.toString()
    });
    
    return this.callEdgeFunction(`locations?${params.toString()}`);
  }

  async createLocation(location: {
    name: string;
    address?: string;
    location_lon?: number;
    location_lat?: number;
    location_type?: string;
    cluster_id?: number;
    project_id: number;
    scenario_id: number;  // REQUIRED: Must specify scenario context
    dataset_id: number;   // REQUIRED: Must specify dataset context
    dataset_name?: string;
    dataset_version?: number;
  }): Promise<any> {
    // Validate required context before API call
    if (!location.scenario_id) {
      throw new Error('scenario_id is required for location creation');
    }
    if (!location.dataset_id) {
      throw new Error('dataset_id is required for location creation');
    }
    return this.callEdgeFunction('locations', {
      method: 'POST',
      body: JSON.stringify(location),
    });
  }

  async updateLocation(locationId: number, projectId: number, updates: {
    name?: string;
    address?: string;
    location_lon?: number;
    location_lat?: number;
    location_type?: string;
    cluster_id?: number;
  }): Promise<any> {
    const params = new URLSearchParams({
      id: locationId.toString(),
      project_id: projectId.toString()
    });
    
    return this.callEdgeFunction(`locations?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteLocation(locationId: number, projectId: number): Promise<any> {
    console.log('🗺️ DELETE Location API Call:', { locationId, projectId });
    
    // Use proper query parameters without cache busting
    const params = new URLSearchParams({
      id: locationId.toString(),
      project_id: projectId.toString()
    });
    
    return this.callEdgeFunction(`locations?${params.toString()}`, {
      method: 'DELETE',
    });
  }

  // Bulk operations for locations
  async bulkCreateLocations(locations: any[], projectId: number, scenarioId: number, datasetId: number): Promise<any[]> {
    // Validate required context
    if (!scenarioId) {
      throw new Error('scenario_id is required for bulk location creation');
    }
    if (!datasetId) {
      throw new Error('dataset_id is required for bulk location creation');
    }
    
    return this.callEdgeFunction('locations/bulk', {
      method: 'POST',
      body: JSON.stringify({ 
        locations, 
        project_id: projectId,
        scenario_id: scenarioId,
        dataset_id: datasetId
      }),
    });
  }

  // Jobs API - Using Edge Functions with scenario context
  async getJobs(projectId: number, options?: {
    search?: string;
    page?: number;
    limit?: number;
    scenario_id?: number;
    dataset_name?: string;
    dataset_version?: number;
    dataset_id?: number;
    include_all_project_data?: boolean;
  }): Promise<any> {
    const params = new URLSearchParams({ project_id: projectId.toString() });
    
    if (options?.search) params.append('search', options.search);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.scenario_id) {
      // Defensive check to ensure scenario_id is a number, not an object
      if (typeof options.scenario_id === 'object') {
        console.error('❌ [API] scenario_id is an object instead of number:', options.scenario_id);
        // Additional debugging info
        console.error('❌ [API] Object keys:', Object.keys(options.scenario_id));
        console.error('❌ [API] Object values:', Object.values(options.scenario_id));
        console.error('❌ [API] Full options object:', options);
        throw new Error('Invalid scenario_id: expected number, got object. Check scenario context usage.');
      }
      // Also check for undefined/null/NaN values
      if (options.scenario_id == null || isNaN(options.scenario_id)) {
        console.error('❌ [API] scenario_id is invalid:', options.scenario_id);
        throw new Error('Invalid scenario_id: must be a valid number');
      }
      params.append('scenario_id', options.scenario_id.toString());
    }
    if (options?.dataset_id) {
      // Defensive check to ensure dataset_id is a number, not an object
      if (typeof options.dataset_id === 'object') {
        console.error('❌ [API] dataset_id is an object instead of number:', options.dataset_id);
        // Additional debugging info
        console.error('❌ [API] Object keys:', Object.keys(options.dataset_id));
        console.error('❌ [API] Object values:', Object.values(options.dataset_id));
        console.error('❌ [API] Full options object:', options);
        throw new Error('Invalid dataset_id: expected number, got object. Check dataset context usage.');
      }
      // Also check for undefined/null/NaN values
      if (options.dataset_id == null || isNaN(options.dataset_id)) {
        console.error('❌ [API] dataset_id is invalid:', options.dataset_id);
        throw new Error('Invalid dataset_id: must be a valid number');
      }
      params.append('dataset_id', options.dataset_id.toString());
    }
    if (options?.dataset_name) params.append('dataset_name', options.dataset_name);
    if (options?.dataset_version) params.append('dataset_version', options.dataset_version.toString());
    if (options?.include_all_project_data) params.append('include_all_project_data', options.include_all_project_data.toString());

    const response = await this.callEdgeFunction(`jobs?${params.toString()}`);
    
    // Unwrap the response if it's in the wrapped format
    if (response && response.success && Array.isArray(response.data)) {
      return response.data;
    }
    
    // Return empty array if no valid response
    return [];
  }

  async getJob(jobId: number, projectId: number): Promise<any> {
    const params = new URLSearchParams({
      project_id: projectId.toString(),
      id: jobId.toString()
    });
    
    return this.callEdgeFunction(`jobs?${params.toString()}`);
  }

  async getJobsWithLocationDependency(projectId: number, options?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<any> {
    return this.getJobs(projectId, options);
  }

  async createJob(job: {
    description: string;
    location_id: number;
    setup?: number;
    service?: number;
    delivery?: number[];
    pickup?: number[];
    skills?: number[];
    priority?: number;
    time_windows?: Array<{ start: number; end: number; }>;
    project_id: number;
    scenario_id: number;  // REQUIRED: Must specify scenario context
    dataset_id: number;   // REQUIRED: Must specify dataset context
    dataset_name?: string;
    dataset_version?: number;
  }): Promise<any> {
    // Validate required context before API call
    if (!job.scenario_id) {
      throw new Error('scenario_id is required for job creation');
    }
    if (!job.dataset_id) {
      throw new Error('dataset_id is required for job creation');
    }
    return this.callEdgeFunction('jobs', {
      method: 'POST',
      body: JSON.stringify(job),
    });
  }

  async updateJob(jobId: number, projectId: number, updates: {
    description?: string;
    location_id?: number;
    setup?: number;
    service?: number;
    delivery?: number[];
    pickup?: number[];
    skills?: number[];
    priority?: number;
    time_windows?: Array<{ start: number; end: number; }>;
  }): Promise<any> {
    const params = new URLSearchParams({
      id: jobId.toString(),
      project_id: projectId.toString()
    });
    
    console.log('💼 UPDATE Job API Call:', `jobs?${params.toString()}`);
    return this.callEdgeFunction(`jobs?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteJob(jobId: number, projectId: number): Promise<any> {
    const params = new URLSearchParams({
      id: jobId.toString(),
      project_id: projectId.toString()
    });
    
    return this.callEdgeFunction(`jobs?${params.toString()}`, {
      method: 'DELETE',
    });
  }

  // Bulk operations for jobs
  async bulkCreateJobs(jobs: any[], projectId: number, scenarioId: number, datasetId: number): Promise<any[]> {
    // Validate required context
    if (!scenarioId) {
      throw new Error('scenario_id is required for bulk job creation');
    }
    if (!datasetId) {
      throw new Error('dataset_id is required for bulk job creation');
    }
    
    return this.callEdgeFunction('jobs/bulk', {
      method: 'POST',
      body: JSON.stringify({ 
        jobs, 
        project_id: projectId,
        scenario_id: scenarioId,
        dataset_id: datasetId
      }),
    });
  }

  // Datasets API - Using Edge Functions (CORRECTED FROM CRITICAL ERROR)
  async getDatasets(scenarioId: number, options?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<any[]> {
    const params = new URLSearchParams({
      scenario_id: scenarioId.toString()
    });
    
    if (options?.search) params.append('search', options.search);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await this.callEdgeFunction(`datasets?${params.toString()}`);
    
    // Unwrap the response if it's in the wrapped format
    if (response && response.success && Array.isArray(response.data)) {
      return response.data;
    }
    
    // Return empty array if no valid response
    return [];
  }

  async getDataset(datasetId: number): Promise<any> {
    const params = new URLSearchParams({
      id: datasetId.toString()
    });
    return this.callEdgeFunction(`datasets?${params.toString()}`);
  }

  async createDataset(dataset: {
    name: string;
    description?: string;
    scenario_id: number;
    version?: string;
    metadata?: Record<string, any>;
  }): Promise<any> {
    return this.callEdgeFunction('datasets', {
      method: 'POST',
      body: JSON.stringify({
        name: dataset.name,
        description: dataset.description,
        scenario_id: dataset.scenario_id,
        dataset_type: 'complete',
        version_note: dataset.version || '1.0.0'
      }),
    });
  }

  async updateDataset(datasetId: number, updates: {
    name?: string;
    description?: string;
    metadata?: Record<string, any>;
  }): Promise<any> {
    const params = new URLSearchParams({
      id: datasetId.toString()
    });
    return this.callEdgeFunction(`datasets?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteDataset(datasetId: number): Promise<void> {
    const params = new URLSearchParams({
      id: datasetId.toString()
    });
    return this.callEdgeFunction(`datasets?${params.toString()}`, {
      method: 'DELETE',
    });
  }

  // Public test function - No authentication required
  async testPublicAccess(): Promise<any> {
    return this.callPublicEdgeFunction('frontend-test');
  }

  // ALL dataset operations - Using Edge Functions ONLY
  async copyDataset(datasetId: number, targetScenarioId: number, newName?: string, newDescription?: string): Promise<any> {
    return this.callEdgeFunction('datasets/copy', {
      method: 'POST',
      body: JSON.stringify({
        source_dataset_id: datasetId,
        target_scenario_id: targetScenarioId,
        new_dataset_name: newName,
        description: newDescription
      }),
    });
  }

  async createDatasetVersion(datasetId: number, newVersion: string, description?: string): Promise<any> {
    return this.callEdgeFunction('datasets/version', {
      method: 'POST',
      body: JSON.stringify({
        dataset_id: datasetId,
        new_version: newVersion,
        description: description
      }),
    });
  }

  async getDatasetStatistics(datasetId: number): Promise<any> {
    const params = new URLSearchParams({
      dataset_id: datasetId.toString()
    });
    
    const response = await this.callEdgeFunction(`datasets/statistics?${params.toString()}`);
    
    if (response?.success && response?.data) {
      // Transform Edge Function response to expected DatasetStatistics format
      const edgeData = response.data;
      const entityCounts = edgeData.entity_counts || { vehicles: 0, jobs: 0, locations: 0 };
      
      return {
        dataset_id: edgeData.dataset_id,
        dataset_name: `Dataset ${edgeData.dataset_id}`, // Default name since not provided
        dataset_version: 1, // Default version
        vehicle_count: entityCounts.vehicles || 0,
        job_count: entityCounts.jobs || 0,
        location_count: entityCounts.locations || 0,
        total_capacity: [], // Will be computed when needed
        total_demand: [], // Will be computed when needed
        geographic_bounds: {
          min_lat: 0,
          max_lat: 0,
          min_lon: 0,
          max_lon: 0
        }, // Will be computed when needed
        optimization_count: edgeData.optimization_count || 0,
        avg_optimization_time: 'N/A',
        last_optimization_at: null,
        data_hash: null,
        last_updated: new Date().toISOString()
      };
    }
    
    return response;
  }

  async refreshDatasetStatistics(datasetId: number, datasetName?: string, scenarioId?: number): Promise<any> {
    console.log('📊 Refreshing dataset statistics:', datasetId, datasetName, scenarioId);
    
    const response = await this.callEdgeFunction('datasets/refresh-statistics', {
      method: 'POST',
      body: JSON.stringify({
        dataset_id: datasetId,
        name: datasetName || `Dataset ${datasetId}`,
        scenario_id: scenarioId || 1
      }),
    });
    
    if (response?.success && response?.data) {
      // Transform Edge Function response to expected DatasetStatistics format
      const edgeData = response.data;
      const entityCounts = edgeData.entity_counts || { vehicles: 0, jobs: 0, locations: 0 };
      
      return {
        dataset_id: edgeData.dataset_id,
        dataset_name: `Dataset ${edgeData.dataset_id}`, // Default name since not provided
        dataset_version: 1, // Default version
        vehicle_count: entityCounts.vehicles || 0,
        job_count: entityCounts.jobs || 0,
        location_count: entityCounts.locations || 0,
        total_capacity: [], // Will be computed when needed
        total_demand: [], // Will be computed when needed
        geographic_bounds: {
          min_lat: 0,
          max_lat: 0,
          min_lon: 0,
          max_lon: 0
        }, // Will be computed when needed
        optimization_count: edgeData.optimization_count || 0,
        avg_optimization_time: 'N/A',
        last_optimization_at: null,
        data_hash: null,
        last_updated: new Date().toISOString()
      };
    }
    
    return response;
  }

  // =============================================
  // OPTIMIZATION RESULTS WITH DATASET CONTEXT
  // =============================================

  // Store optimization results with full dataset context
  async storeOptimizationResults(results: {
    project_id: number;
    scenario_id: number;
    dataset_id: number;
    dataset_name: string;
    dataset_version: string;
    optimization_data: any; // VROOM results
    metadata?: {
      algorithm: string;
      duration_ms: number;
      settings: any;
    };
  }): Promise<any> {
    console.log('💾 [API] Storing optimization results with context:', {
      scenario_id: results.scenario_id,
      dataset_id: results.dataset_id,
      dataset_name: results.dataset_name
    });

    return this.callEdgeFunction('optimization-results', {
      method: 'POST',
      body: JSON.stringify(results),
    });
  }

  // Get optimization results for a specific dataset
  async getOptimizationResults(datasetId: number, options?: {
    limit?: number;
    offset?: number;
  }): Promise<any[]> {
    const params = new URLSearchParams({
      dataset_id: datasetId.toString()
    });
    
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    return this.callEdgeFunction(`optimization/results?${params.toString()}`);
  }

  // Get optimization history for a scenario (across all datasets)
  async getScenarioOptimizationHistory(scenarioId: number, options?: {
    limit?: number;
    offset?: number;
  }): Promise<any[]> {
    const params = new URLSearchParams({
      scenario_id: scenarioId.toString()
    });
    
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    return this.callEdgeFunction(`optimization/history?${params.toString()}`);
  }

  // =============================================
  // DATASET-SPECIFIC DATA LOADING
  // =============================================

  // Load vehicles for a specific dataset context
  async getDatasetVehicles(datasetId: number, projectId: number): Promise<any[]> {
    const params = new URLSearchParams({
      project_id: projectId.toString(),
      dataset_id: datasetId.toString()
    });
    
    return this.callEdgeFunction(`vehicles?${params.toString()}`);
  }

  // Load jobs for a specific dataset context
  async getDatasetJobs(datasetId: number, projectId: number): Promise<any[]> {
    const params = new URLSearchParams({
      project_id: projectId.toString(),
      dataset_id: datasetId.toString()
    });
    
    return this.callEdgeFunction(`jobs?${params.toString()}`);
  }

  // Load locations for a specific dataset context
  async getDatasetLocations(datasetId: number, projectId: number): Promise<any[]> {
    const params = new URLSearchParams({
      project_id: projectId.toString(),
      dataset_id: datasetId.toString()
    });
    
    return this.callEdgeFunction(`locations?${params.toString()}`);
  }

  // Get complete dataset context information
  async getDatasetContext(datasetId: number): Promise<{
    dataset: any;
    scenario: any;
    project: any;
    statistics: any;
  }> {
    const params = new URLSearchParams({
      dataset_id: datasetId.toString()
    });
    
    return this.callEdgeFunction(`datasets/context?${params.toString()}`);
  }

  // =============================================
  // SCENARIO-BASED DATA INHERITANCE
  // =============================================

  // Copy data between datasets for scenario inheritance workflows
  async copyDatasetData(request: {
    source_dataset_id: number;
    target_dataset_id: number;
    data_types: ('locations' | 'vehicles' | 'jobs')[];
    options?: {
      copy_all?: boolean;
      filter_by_type?: string;
      preserve_ids?: boolean;
    };
  }): Promise<{
    success: boolean;
    copied_counts: {
      locations: number;
      vehicles: number;
      jobs: number;
    };
    target_dataset_id: number;
    execution_time_ms: number;
  }> {
    console.log('🔄 [API] Copying dataset data:', {
      source: request.source_dataset_id,
      target: request.target_dataset_id,
      types: request.data_types
    });

    return this.callEdgeFunction('datasets/copy-data', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Create scenario with inherited data (convenience method)
  async createScenarioWithInheritance(request: {
    scenario_name: string;
    scenario_description?: string;
    project_id: number;
    source_scenario_id: number;
    inherit_data_types: ('locations' | 'vehicles' | 'jobs')[];
    planning_horizon_days?: number;
  }): Promise<{
    scenario: any;
    dataset: any;
    inheritance_result: any;
  }> {
    console.log('🎯 [API] Creating scenario with data inheritance:', {
      name: request.scenario_name,
      source_scenario: request.source_scenario_id,
      inherit_types: request.inherit_data_types
    });

    // Step 1: Create new scenario
    console.log('🎯 [INHERITANCE] Step 1 - Creating scenario...');
    const scenarioResponse = await this.createScenario({
      name: request.scenario_name,
      description: request.scenario_description || '',
      project_id: request.project_id,
      start_date: new Date().toISOString().split('T')[0],
      planning_horizon_days: request.planning_horizon_days || 30
    });

    console.log('🎯 [INHERITANCE] Step 1 - Scenario Response:', scenarioResponse);

    // Extract scenario data from Edge Function response format with strict validation
    const newScenario = scenarioResponse?.data;
    if (!newScenario?.id) {
      console.error('❌ [INHERITANCE] Invalid scenario response:', scenarioResponse);
      throw new Error(`Failed to create scenario - invalid response: ${JSON.stringify(scenarioResponse)}`);
    }

    console.log('✅ [INHERITANCE] Step 1 - Scenario created:', newScenario.id, newScenario.name);

    // Step 2: Create dataset for new scenario
    console.log('🎯 [INHERITANCE] Step 2 - Creating dataset...');
    const datasetResponse = await this.createDataset({
      name: `${request.scenario_name} Dataset`,
      description: `Dataset for ${request.scenario_name} scenario`,
      scenario_id: newScenario.id
    });

    console.log('🎯 [INHERITANCE] Step 2 - Dataset Response:', datasetResponse);

    // Extract dataset data from Edge Function response format with strict validation
    const newDataset = datasetResponse?.data;
    if (!newDataset?.id) {
      console.error('❌ [INHERITANCE] Invalid dataset response:', datasetResponse);
      throw new Error(`Failed to create dataset - invalid response: ${JSON.stringify(datasetResponse)}`);
    }

    console.log('✅ [INHERITANCE] Step 2 - Dataset created:', newDataset.id, newDataset.name);

    // Step 3: Get source dataset with intelligent selection
    console.log('🎯 [INHERITANCE] Step 3 - Getting source datasets...');
    const datasetsResponse = await this.getDatasets(request.source_scenario_id);
    console.log('🎯 [INHERITANCE] Step 3 - Datasets Response:', datasetsResponse);

    // Handle Edge Function response format with strict validation
    const sourceDatasets = datasetsResponse?.data;
    if (!Array.isArray(sourceDatasets) || sourceDatasets.length === 0) {
      console.error('❌ [INHERITANCE] No datasets found in source scenario:', {
        source_scenario_id: request.source_scenario_id,
        datasetsResponse
      });
      throw new Error(`No datasets found in source scenario ${request.source_scenario_id}`);
    }

    // Select dataset with most data, not just first
    const sourceDataset = sourceDatasets.reduce((best, current) => {
      const bestCount = (best.entity_counts?.locations || 0) + (best.entity_counts?.vehicles || 0) + (best.entity_counts?.jobs || 0);
      const currentCount = (current.entity_counts?.locations || 0) + (current.entity_counts?.vehicles || 0) + (current.entity_counts?.jobs || 0);
      return currentCount > bestCount ? current : best;
    });

    console.log('✅ [INHERITANCE] Step 3 - Selected source dataset:', {
      id: sourceDataset.id,
      name: sourceDataset.name,
      entity_counts: sourceDataset.entity_counts
    });

    // Step 4: Copy specified data types
    console.log('🎯 [INHERITANCE] Step 4 - Copying data types:', request.inherit_data_types);
    const copyRequest = {
      source_dataset_id: sourceDataset.id,
      target_dataset_id: newDataset.id,
      data_types: request.inherit_data_types
    };
    console.log('🎯 [INHERITANCE] Step 4 - Copy Request:', copyRequest);

    const inheritanceResponse = await this.copyDatasetData(copyRequest);
    console.log('🎯 [INHERITANCE] Step 4 - Copy Response:', inheritanceResponse);

    // Handle Edge Function response format for inheritance result with validation
    const inheritanceResult = inheritanceResponse?.data || inheritanceResponse;
    if (!inheritanceResult?.success) {
      console.error('❌ [INHERITANCE] Copy operation failed:', inheritanceResponse);
      throw new Error(`Data copying failed: ${JSON.stringify(inheritanceResponse)}`);
    }

    console.log('✅ [INHERITANCE] Step 4 - Data copied successfully:', inheritanceResult.copied_counts);

    return {
      scenario: newScenario,
      dataset: newDataset,
      inheritance_result: inheritanceResult
    };
  }

  // =============================================
  // ENHANCED VEHICLE API - DAY 2 MPAD INTEGRATION
  // =============================================

  // Enhanced Vehicles API with multi-dimensional capacity and skills
  async getEnhancedVehicles(projectId: number, options?: {
    search?: string;
    page?: number;
    limit?: number;
    include_capacity?: boolean;
    include_skills?: boolean;
    include_vroom_compatibility?: boolean;
  }): Promise<any[]> {
    const params = new URLSearchParams({ project_id: projectId.toString() });
    
    if (options?.search) params.append('search', options.search);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.include_capacity) params.append('include_capacity', 'true');
    if (options?.include_skills) params.append('include_skills', 'true');
    if (options?.include_vroom_compatibility) params.append('include_vroom_compatibility', 'true');

    console.log('🚗 [API] Enhanced Vehicles Call:', `vehicles-enhanced?${params.toString()}`);
    return this.callEdgeFunction(`vehicles-enhanced?${params.toString()}`);
  }

  async getEnhancedVehicle(vehicleId: number, projectId: number): Promise<any> {
    const params = new URLSearchParams({
      project_id: projectId.toString(),
      vehicle_id: vehicleId.toString(),
      include_capacity: 'true',
      include_skills: 'true',
      include_vroom_compatibility: 'true'
    });
    
    console.log('🚗 [API] Enhanced Vehicle Call:', `vehicles-enhanced?${params.toString()}`);
    return this.callEdgeFunction(`vehicles-enhanced?${params.toString()}`);
  }

  async updateEnhancedVehicle(vehicleId: number, projectId: number, data: {
    name?: string;
    description?: string;
    profile?: string;
    start_location_id?: number;
    end_location_id?: number;
    time_window?: [number, number];
    cost_fixed?: number;
    cost_per_hour?: number;
    cost_per_km?: number;
    speed_factor?: number;
    max_tasks?: number;
    capacity_array?: number[];
    skills?: number[];
  }): Promise<any> {
    const params = new URLSearchParams({
      vehicle_id: vehicleId.toString(),
      project_id: projectId.toString()
    });
    
    console.log('🚗 [API] Update Enhanced Vehicle:', `vehicles-enhanced?${params.toString()}`);
    return this.callEdgeFunction(`vehicles-enhanced?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // =============================================
  // CAPACITY MANAGEMENT API
  // =============================================

  // Capacity Definitions Management
  async getCapacityDefinitions(): Promise<any[]> {
    console.log('📦 [API] Getting capacity definitions');
    return this.callEdgeFunction('capacity-management/definitions');
  }

  async createCapacityDefinition(data: {
    name: string;
    unit: string;
    description?: string;
  }): Promise<any> {
    console.log('📦 [API] Creating capacity definition:', data);
    return this.callEdgeFunction('capacity-management/definitions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCapacityDefinition(id: number, data: {
    name?: string;
    unit?: string;
    description?: string;
  }): Promise<any> {
    console.log('📦 [API] Updating capacity definition:', id, data);
    return this.callEdgeFunction('capacity-management/definitions', {
      method: 'PUT',
      body: JSON.stringify({ id, ...data }),
    });
  }

  // Bulk Capacity Operations
  async bulkUpdateVehicleCapacities(vehicleIds: number[], capacityData: {
    weight?: number;
    volume?: number;
    pallets?: number;
    items?: number;
  }): Promise<any> {
    console.log('📦 [API] Bulk updating vehicle capacities:', { vehicleIds, capacityData });
    return this.callEdgeFunction('capacity-management/bulk-operations', {
      method: 'POST',
      body: JSON.stringify({
        operation: 'bulk_update_capacities',
        vehicleIds,
        capacityData
      }),
    });
  }

  async activateCapacitySystem(projectId: number): Promise<any> {
    console.log('📦 [API] Activating capacity system for project:', projectId);
    return this.callEdgeFunction('capacity-management/bulk-operations', {
      method: 'POST',
      body: JSON.stringify({
        operation: 'activate_capacity_system',
        projectId
      }),
    });
  }

  // Capacity Validation
  async validateCapacityConsistency(projectId: number): Promise<any> {
    console.log('📦 [API] Validating capacity consistency for project:', projectId);
    return this.callEdgeFunction('capacity-management/validation', {
      method: 'POST',
      body: JSON.stringify({
        projectId,
        validationType: 'capacity_consistency'
      }),
    });
  }

  async validateVROOMCompatibility(projectId: number): Promise<any> {
    console.log('📦 [API] Validating VROOM compatibility for project:', projectId);
    return this.callEdgeFunction('capacity-management/validation', {
      method: 'POST',
      body: JSON.stringify({
        projectId,
        validationType: 'vroom_compatibility'
      }),
    });
  }

  // =============================================
  // SKILLS MANAGEMENT API
  // =============================================

  // Skills CRUD
  async getSkills(): Promise<any[]> {
    console.log('🎯 [API] Getting all skills');
    return this.callEdgeFunction('skills-management/skills');
  }

  async createSkill(data: {
    name: string;
    description?: string;
    category?: string;
  }): Promise<any> {
    console.log('🎯 [API] Creating skill:', data);
    return this.callEdgeFunction('skills-management/skills', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSkill(id: number, data: {
    name?: string;
    description?: string;
    category?: string;
  }): Promise<any> {
    console.log('🎯 [API] Updating skill:', id, data);
    return this.callEdgeFunction('skills-management/skills', {
      method: 'PUT',
      body: JSON.stringify({ id, ...data }),
    });
  }

  async deleteSkill(id: number): Promise<void> {
    console.log('🎯 [API] Deleting skill:', id);
    return this.callEdgeFunction('skills-management/skills', {
      method: 'DELETE',
      body: JSON.stringify({ id }),
    });
  }

  // Vehicle Skills Management
  async getVehicleSkills(vehicleId: number): Promise<any[]> {
    const params = new URLSearchParams({
      vehicle_id: vehicleId.toString()
    });
    
    console.log('🎯 [API] Getting vehicle skills:', vehicleId);
    return this.callEdgeFunction(`skills-management/vehicle-skills?${params.toString()}`);
  }

  async assignSkillToVehicle(data: {
    vehicle_id: number;
    skill_id: number;
    proficiency_level?: string;
  }): Promise<any> {
    console.log('🎯 [API] Assigning skill to vehicle:', data);
    return this.callEdgeFunction('skills-management/vehicle-skills', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async removeSkillFromVehicle(vehicleId: number, skillId: number): Promise<void> {
    console.log('🎯 [API] Removing skill from vehicle:', { vehicleId, skillId });
    return this.callEdgeFunction('skills-management/vehicle-skills', {
      method: 'DELETE',
      body: JSON.stringify({
        vehicle_id: vehicleId,
        skill_id: skillId
      }),
    });
  }

  // Job Skill Requirements
  async getJobSkillRequirements(jobId: number): Promise<any[]> {
    const params = new URLSearchParams({
      job_id: jobId.toString()
    });
    
    console.log('🎯 [API] Getting job skill requirements:', jobId);
    return this.callEdgeFunction(`skills-management/job-requirements?${params.toString()}`);
  }

  async addJobSkillRequirement(data: {
    job_id: number;
    skill_id: number;
    required_level?: string;
  }): Promise<any> {
    console.log('🎯 [API] Adding job skill requirement:', data);
    return this.callEdgeFunction('skills-management/job-requirements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async removeJobSkillRequirement(jobId: number, skillId: number): Promise<void> {
    console.log('🎯 [API] Removing job skill requirement:', { jobId, skillId });
    return this.callEdgeFunction('skills-management/job-requirements', {
      method: 'DELETE',
      body: JSON.stringify({
        job_id: jobId,
        skill_id: skillId
      }),
    });
  }

  // Skill Compatibility Analysis
  async analyzeProjectSkillCompatibility(projectId: number): Promise<any> {
    console.log('🎯 [API] Analyzing project skill compatibility:', projectId);
    return this.callEdgeFunction('skills-management/compatibility', {
      method: 'POST',
      body: JSON.stringify({
        projectId
      }),
    });
  }

  async checkVehicleJobCompatibility(vehicleId: number, jobId: number): Promise<any> {
    console.log('🎯 [API] Checking vehicle-job compatibility:', { vehicleId, jobId });
    return this.callEdgeFunction('skills-management/compatibility', {
      method: 'POST',
      body: JSON.stringify({
        vehicleId,
        jobId
      }),
    });
  }

  // =============================================
  // LEGACY VEHICLE SKILLS API (DEPRECATED - FOR BACKWARD COMPATIBILITY)
  // =============================================

  async updateVehicleSkill(vehicleSkillId: number, data: {
    proficiency_level?: number;
    certification_number?: string;
    certification_expiry_date?: string;
    verified?: boolean;
    notes?: string;
  }): Promise<any> {
    console.log('🎯 [API] Updating vehicle skill (legacy):', vehicleSkillId, data);
    
    const params = new URLSearchParams({
      id: vehicleSkillId.toString()
    });
    
    return this.callEdgeFunction(`vehicle-skills?${params.toString()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
}

// Export singleton instance
export const apiClient = new APIClient();
export default apiClient;
